import { supabase } from '../supabase';
import type { UserCredits, CreditTransaction, PaginatedResponse } from '@/types/database.types';

/**
 * Get the current user's credit balance and related information
 * @returns User credits information
 */
export async function getUserCredits(): Promise<UserCredits> {
  try {
    // First, check if the user has a credits record
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if user has a credits record
    const { data: existingCredits, error: checkError } = await supabase
      .from('user_credits')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    // If no credits record exists, create one
    if (checkError || !existingCredits) {
      // console.log('No credits record found, creating one...');
      
      // Create a new credits record
      const { error: insertError } = await supabase
        .from('user_credits')
        .insert([
          { 
            user_id: user.id,
            balance: 0.00,
            lifetime_earned: 0.00,
            lifetime_withdrawn: 0.00
          }
        ]);
      
      if (insertError) {
        console.error('Error creating credits record:', insertError);
        // Continue anyway, we'll use the RPC function as fallback
      }
    }
    
    // Use the RPC function to get credits (this will handle creation if needed)
    const { data, error } = await supabase.rpc('get_user_credits');
    
    if (error) {
      console.error('Error fetching user credits:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      throw new Error('No data returned from get_user_credits');
    }
    
    // Log the raw data for debugging
    // console.log('Raw user credits data:', data);
    
    // The data comes back as an array with a single object
    const creditData = Array.isArray(data) ? data[0] : data;
    
    // Ensure all numeric values are properly parsed
    const balance = parseFloat(creditData.balance) || 0;
    const lifetime_earned = parseFloat(creditData.lifetime_earned) || 0;
    const lifetime_withdrawn = parseFloat(creditData.lifetime_withdrawn) || 0;
    const min_cashout_amount = parseFloat(creditData.min_cashout_amount) || 5.00;
    
    // Log the parsed values for debugging
    // console.log('Parsed credit values:', {
    //   balance,
    //   lifetime_earned,
    //   lifetime_withdrawn,
    //   min_cashout_amount
    // });
    
    return {
      balance,
      lifetime_earned,
      lifetime_withdrawn,
      eligible_for_cashout: !!creditData.eligible_for_cashout,
      min_cashout_amount
    };
  } catch (error) {
    console.error('Error in getUserCredits:', error);
    // Return default values on error
    return {
      balance: 0,
      lifetime_earned: 0,
      lifetime_withdrawn: 0,
      eligible_for_cashout: false,
      min_cashout_amount: 5.00
    };
  }
}

/**
 * Get the user's credit transaction history
 * @param page Page number (starting from 1)
 * @param limit Number of transactions per page
 * @returns Paginated list of credit transactions
 */
export async function getCreditTransactions(
  page = 1,
  limit = 10
): Promise<PaginatedResponse<CreditTransaction>> {
  try {
    const offset = (page - 1) * limit;
    
    const { data, error } = await supabase.rpc('get_credit_transactions', {
      p_limit: limit,
      p_offset: offset
    });
    
    if (error) {
      console.error('Error fetching credit transactions:', error);
      throw error;
    }
    
    // Get total count
    const { count, error: countError } = await supabase
      .from('credit_transactions')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', (await supabase.auth.getUser()).data.user?.id);
    
    if (countError) {
      console.error('Error fetching transaction count:', countError);
      throw countError;
    }
    
    // Ensure all numeric values are properly parsed
    const formattedData = data.map((transaction: any) => ({
      ...transaction,
      amount: parseFloat(transaction.amount) || 0
    }));
    
    return {
      data: formattedData,
      total: count || 0,
      page,
      limit
    };
  } catch (error) {
    console.error('Error in getCreditTransactions:', error);
    return {
      data: [],
      total: 0,
      page,
      limit
    };
  }
}

/**
 * Request a cashout of credits
 * @param amount Amount to cash out
 * @returns True if the request was successful
 */
export async function requestCashout(amount: number): Promise<boolean> {
  const { data, error } = await supabase.rpc('request_cashout', {
    p_amount: amount
  });
  
  if (error) {
    console.error('Error requesting cashout:', error);
    throw error;
  }
  
  return data;
}